import type { QueryClient } from "@tanstack/react-query"
import { createRouter } from "@tanstack/react-router"

import { EmptyState } from "@/components/common/empty-state"
import { RouteError } from "@/components/common/route-error"
import { Spinner } from "@/components/ui/spinner"
import { routeTree } from "@/routeTree.gen"

export type RouterContext = {
  queryClient: QueryClient
}

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    /**
     * Zero, because TanStack Query already owns freshness. Letting the router
     * keep its own copy of loader data would give us two caches disagreeing.
     */
    defaultPreloadStaleTime: 0,
    defaultPendingMs: 200,
    defaultPendingMinMs: 300,
    scrollRestoration: true,
    defaultPendingComponent: () => (
      <div className="flex flex-1 items-center justify-center p-12">
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    ),
    defaultErrorComponent: RouteError,
    defaultNotFoundComponent: () => (
      <EmptyState
        title="Page not found"
        description="The page you were looking for does not exist."
      />
    ),
  })
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>
  }

  interface StaticDataRouteOption {
    /**
     * Whether the page sits inside the shell's padding or fills the sheet
     * below the header, edge to edge (a document on its desk, an editor).
     * The shell decides; a page never cancels the padding itself.
     */
    content?: "padded" | "edge-to-edge"
    /**
     * Which part of the record this page shows, as a section key.
     *
     * The workspace reads it and puts what is outstanding about that section
     * in one fixed place above the content. A page names its section and gets
     * the band; it never renders one itself, which is why they used to sit in
     * four different places at four different widths.
     */
    attention?: string
  }
}
